import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import type { FieldEditState } from '../store/entries-edit.slice'
import { EditableEntryField } from '../editable-field-config'

dayjs.extend(customParseFormat)

/**
 * Check if the given cell coordinates match the currently active edit
 *
 * Best used INSIDE a useAppSelector callback so the selector returns a primitive boolean.
 * This way, only the cell that transitions between true↔false re-renders — not every cell
 * on every keystroke (which would happen if the selector returned the full activeEdit object).
 *
 * @param activeEdit - The currently active edit
 * @param parentId - The ID of the billing summary asset
 * @param entryId - The ID of the medical billing summary entry
 * @param fieldKey - The key of the editable field
 * @returns True if the cell is being edited, false otherwise
 */
export function isCellBeingEdited(activeEdit: FieldEditState | null, parentId: string | undefined, entryId: string, fieldKey: EditableEntryField): boolean {
  if (!activeEdit || !parentId) return false

  return activeEdit.parentId === parentId && activeEdit.entryId === entryId && activeEdit.fieldKey === fieldKey
}

/**
 * Returns true if pending and original are the same for save purposes
 *
 * Owns the rule for "no real change" so we don't fire extra API calls when the value is effectively unchanged.
 * - Amount: "25" and "25.00" are the same
 * - Other fields: trim and strict equality
 */
export function pendingValueEqualsOriginal(fieldKey: EditableEntryField, original: string, pending: string): boolean {
  const o = original.trim()
  const p = pending.trim()

  if (fieldKey === EditableEntryField.AMOUNT) {
    if (p.length === 0 && o.length === 0) return true
    const pNum = parseFloat(p)
    const oNum = parseFloat(o)
    if (Number.isNaN(pNum) || Number.isNaN(oNum)) return p === o
    return pNum === oNum
  }
  return p === o
}

/**
 * Prepare a field value for saving to the API
 * - Trims whitespace
 * - Converts empty strings to null
 * - Converts SERVICE_DATE from 'MM/DD/YYYY' to ISO format ('YYYY-MM-DD')
 * - Converts AMOUNT to a number
 *
 * @param fieldKey - The key of the editable field
 * @param value - The value to prepare
 * @returns The prepared value (string, number, or null if empty)
 */
export function prepareValueForSave(fieldKey: EditableEntryField, value: string): string | number | null {
  const trimmed = value.trim()

  // Convert empty strings to null
  if (trimmed.length === 0) {
    return null
  }

  if (fieldKey === EditableEntryField.SERVICE_DATE) {
    return dayjs(trimmed, 'MM/DD/YYYY').format('YYYY-MM-DD')
  }

  if (fieldKey === EditableEntryField.AMOUNT) {
    return parseFloat(trimmed)
  }

  return trimmed
}
